/**
 * Market — audience + monetization dimension, orthogonal to `locale` (language).
 *
 *   locale  (`id`/`en`)            = language, URL-driven (`/[locale]/...`).
 *   market  (`indonesia`/`global`) = audience, geo-derived + override cookie,
 *                                    NEVER a URL segment.
 *
 * `market` is a request-scoped value threaded as a prop the same way `locale`
 * is today (mirrors `audienceForLocale()` in `src/lib/insights/market-pulse.ts`).
 * It exists so non-ID traffic can be served neutral framing + USD monetization
 * while the Indonesia experience stays byte-for-byte unchanged.
 *
 * This module is the SINGLE SOURCE OF TRUTH for the resolution rule and is kept
 * pure (no I/O) so it is trivially unit-testable. The server-side reader that
 * wires it to Cloudflare geo + the request cookie lives in `./server.ts`.
 */

import type { Locale } from "@/lib/i18n/config";

export const markets = ["indonesia", "global"] as const;
export type Market = (typeof markets)[number];

/** Name of the override cookie that pins an explicit market choice. */
export const MARKET_COOKIE = "market";

/** Override cookie lifetime — 1 year (an explicit user choice is durable). */
export const MARKET_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * "Resolved market" hint cookie. It is NOT an override input — it lets CLIENT
 * components on statically-cached / ISR pages (which cannot read geo server-side
 * without forcing dynamic rendering) gate market-specific UI. Not `httpOnly`:
 * client JS must read it.
 *
 * Owned by `<MarketHintProvider>` (src/components/market/market-hint-context.tsx),
 * which resolves the market client-side and writes this cookie. It is deliberately
 * NOT written by middleware: a `Set-Cookie` on a response makes that response
 * uncacheable by Cloudflare's CDN, which previously kept the whole ISR surface off
 * the edge cache. Keeping the page HTML cookie-free is what makes it edge-cacheable.
 */
export const MARKET_HINT_COOKIE = "market_resolved";

/** Type guard: is the string a supported market? */
export function isValidMarket(value: string): value is Market {
	return (markets as readonly string[]).includes(value);
}

/** Read an explicit market override from a raw Cookie header value. */
export function getMarketFromCookie(
	cookieHeader: string | null,
): Market | null {
	if (!cookieHeader) return null;
	const match = cookieHeader.match(/(?:^|;\s*)market=([^;]+)/);
	if (match && isValidMarket(match[1])) return match[1] as Market;
	return null;
}

/**
 * Resolve the market for a request. Pure — geo + cookie are passed in.
 *
 * Priority (highest first):
 *   1. locale === "id"            ⇒ indonesia  (SEO safety: the `id` surface is
 *                                   always the Indonesia experience, even when a
 *                                   US-based Googlebot crawls `/id/...`; geo and
 *                                   override are ignored so ID rankings are safe).
 *   2. explicit override cookie   ⇒ that market (the manual switcher's choice).
 *   3. country ID / unknown       ⇒ indonesia  (unknown ⇒ ID — never degrade a
 *                                   real ID user behind a proxy / Tor / `XX`).
 *   4. otherwise                  ⇒ global.
 */
export function resolveMarket(input: {
	countryCode: string | null | undefined;
	overrideCookie: Market | null;
	locale: Locale;
}): Market {
	if (input.locale === "id") return "indonesia";
	if (input.overrideCookie) return input.overrideCookie;

	const cc = input.countryCode?.toUpperCase();
	// `XX` = Cloudflare "unknown", `T1` = Tor exit — treat both as unknown.
	if (!cc || cc === "ID" || cc === "XX" || cc === "T1") return "indonesia";
	return "global";
}
